import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Presentation, Command as CommandIcon, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BrandWordmark } from "@/components/BrandMark";
import { toast } from "sonner";
import { NAV_CATALOG } from "@/components/navCatalog";
import { usePresentation } from "@/contexts/PresentationContext";

/**
 * Executive Sidebar — Phase XII architectural refinement.
 * 12 permanent sovereign domains + 6 Future Ready reserved placeholders.
 * Every entry maps to an EXISTING route. Zero regression.
 */
export const Sidebar = ({ mobileOpen = false, onClose = () => {} }) => {
  const { user, logout } = useAuth();
  const { presentation, setPresentation } = usePresentation();
  const nav = useNavigate();

  const isAdmin = user?.role === "sovereign_admin" || user?.role === "cadastre_administrator";

  const doLogout = async () => {
    await logout();
    toast.success("Session ended");
    nav("/login");
  };

  const EXEC_DOMAINS = new Set([
    "executive-government", "presidential-cabinet",
    "land-governance", "mineral-governance", "survey-governance",
    "environmental-governance", "national-gis-twin",
    "resource-intelligence", "public-services", "reporting-compliance",
  ]);

  const domains = NAV_CATALOG.filter((d) => {
    if (d.adminOnly && !isAdmin) return false;
    if (presentation && !EXEC_DOMAINS.has(d.id)) return false;
    return true;
  });

  return (
    <aside className={`fixed lg:sticky top-0 z-40 h-screen w-72 shrink-0 border-r cad-hairline
                       bg-[#071B2B] flex flex-col ${mobileOpen ? "left-0" : "-left-full lg:left-0"}
                       transition-[left] duration-200`}>
      <div className="px-5 pt-5 pb-4 border-b cad-hairline">
        <BrandWordmark />
      </div>

      <div className="px-3 py-2 border-b cad-hairline flex items-center gap-1">
        <button
          onClick={() => setPresentation(!presentation)}
          data-testid="toggle-presentation-mode"
          className="flex-1 inline-flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-[10px] font-mono uppercase tracking-[0.14em] border"
          style={{
            background: presentation ? "#7B2FBE30" : "transparent",
            borderColor: presentation ? "#7B2FBE" : "#B3C2CC30",
            color: presentation ? "#F6F8FA" : "#B3C2CC",
          }}
          title="Executive presentation mode — hides technical menus"
        >
          <Presentation size={11} /> Presentation {presentation ? "On" : "Off"}
        </button>
        <div className="inline-flex items-center gap-1 rounded-sm px-2 py-1.5 text-[10px] font-mono uppercase tracking-[0.14em] border border-white/10 text-[#B3C2CC]"
             title="Cmd/Ctrl + K to jump anywhere">
          <CommandIcon size={11} /> K
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-2" data-testid="sidebar-nav">
        {domains.map((d) => (
          <div key={d.id} data-testid={"sidebar-domain-" + d.id}>
            <div className="px-4 pt-3 pb-1 flex items-center gap-2">
              <span className="w-1 h-3 rounded-sm" style={{ background: d.accent }} />
              <span className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: d.accent }}>
                {d.label}
              </span>
              {d.reserved && (
                <span className="ml-auto rounded-sm px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.14em]"
                      style={{ background: d.accent + "30", color: d.accent }}>
                  Future ready
                </span>
              )}
            </div>
            {d.items.map((item) => (
              <NavLink
                key={item.to + "-" + item.label}
                to={item.to}
                end={item.exact === true}
                data-testid={item.testid}
                onClick={onClose}
                className={({ isActive }) => `cad-rail-item ${isActive ? "active" : ""}`}
                style={item.reserved ? { opacity: 0.65 } : undefined}
              >
                <item.icon size={16} strokeWidth={1.6} style={{ color: d.accent }} />
                <span className="flex-1">{item.label}</span>
                {item.reserved && <Lock size={10} className="text-[#7C93A5]" />}
              </NavLink>
            ))}
          </div>
        ))}

        <div className="cad-rail-group">Session</div>
        <div className="px-4 py-2 text-xs text-[#DCE3E8]">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">Signed In</div>
          <div className="text-[#F6F8FA] text-sm mt-1 truncate" data-testid="user-name">{user?.name}</div>
          <div className="text-[10px] text-[#7C93A5] mt-0.5 uppercase tracking-widest font-mono-cad">
            {user?.role?.replace(/_/g, " ")}
          </div>
        </div>
        <button onClick={doLogout} className="cad-rail-item w-full text-left" data-testid="logout-btn">
          <LogOut size={16} strokeWidth={1.6} className="text-[#A93F3F]" />
          Sign out
        </button>
      </nav>
    </aside>
  );
};
